import { useQueryClient } from "@tanstack/react-query";
import { ReactNode, useEffect } from "react";

import {
  isSelectRegistryKey,
  type SelectRegistryKey,
} from "@/config/selectRegistry";
import { useSignalRContext } from "@/contexts/signalRContext";
import getSignalRConnection from "@/helpers/signalr";
import { isExtendedSelectKey } from "@/hooks/useExtendedSelectQuery";
import { extendedSelectKeys } from "@/queries/extendedSelectKeys";
import { selectKeys } from "@/queries/selectKeys";

type SelectSignalRBridgeProps = {
  children: ReactNode;
};

/**
 * Listens to the SignalR hub events ("Refresh", "DataChanged",
 * "RefreshSelect") and invalidates the matching React Query caches.
 */
export const SelectSignalRBridge = ({ children }: SelectSignalRBridgeProps) => {
  const queryClient = useQueryClient();
  const { setConnection, setIsConnected } = useSignalRContext();

  useEffect(() => {
    const connection = getSignalRConnection();
    setConnection(connection);

    const onRefreshAll = () => {
      void queryClient.invalidateQueries({ queryKey: selectKeys.all });
      void queryClient.invalidateQueries({ queryKey: extendedSelectKeys.all });
    };

    const onRefreshSelect = (selectKey?: string) => {
      if (!selectKey) {
        onRefreshAll();
        return;
      }
      if (isSelectRegistryKey(selectKey)) {
        void queryClient.invalidateQueries({
          queryKey: selectKeys.one(selectKey as SelectRegistryKey),
        });
        return;
      }
      if (isExtendedSelectKey(selectKey)) {
        void queryClient.invalidateQueries({
          queryKey: extendedSelectKeys.one(selectKey),
        });
        return;
      }
      onRefreshAll();
    };

    const onReconnected = () => setIsConnected(true);
    const onReconnecting = () => setIsConnected(false);
    const onClose = () => setIsConnected(false);

    connection.on("Refresh", onRefreshAll);
    connection.on("DataChanged", onRefreshAll);
    connection.on("RefreshSelect", onRefreshSelect);
    connection.onreconnected(onReconnected);
    connection.onreconnecting(onReconnecting);
    connection.onclose(onClose);

    // Actually open the channel. Without this, .on(...) handlers are registered
    // but no event ever arrives. `withAutomaticReconnect()` only kicks in AFTER
    // a successful initial start, so we handle the initial failure ourselves.
    let cancelled = false;
    connection
      .start()
      .then(() => {
        if (!cancelled) setIsConnected(true);
      })
      .catch((err: unknown) => {
        console.error("SignalR start failed:", err);
        if (!cancelled) setIsConnected(false);
      });

    return () => {
      cancelled = true;
      connection.off("Refresh", onRefreshAll);
      connection.off("DataChanged", onRefreshAll);
      connection.off("RefreshSelect", onRefreshSelect);
      void connection.stop();
    };
  }, [queryClient, setConnection, setIsConnected]);

  return <>{children}</>;
};
